// src/components/driver/DriverHistory.jsx
import React, { useState } from 'react';
import { 
  Calendar, 
  Filter, 
  Search, 
  ChevronLeft, 
  ChevronRight,
  Package,
  MapPin,
  Clock,
  DollarSign,
  Eye,
  Download,
  X
} from 'lucide-react';
import { 
  formatCurrency, 
  formatDateTime, 
  formatDate,
  getStatusClass 
} from '../../utils/helpers';
import { 
  DELIVERY_STATUS_LABELS, 
  DELIVERY_STATUS 
} from '../../utils/constants';
import Loading from '../common/Loading';

const DriverHistory = ({ 
  history, 
  loading, 
  filters, 
  onFilterChange, 
  onPageChange 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [localFilters, setLocalFilters] = useState({
    status: filters.status || '',
    start_date: filters.start_date || '',
    end_date: filters.end_date || ''
  });

  // Appliquer les filtres
  const handleApplyFilters = () => {
    onFilterChange(localFilters);
    setShowFilters(false);
  };

  // Réinitialiser les filtres
  const handleResetFilters = () => {
    const resetFilters = {
      status: '',
      start_date: '',
      end_date: ''
    };
    setLocalFilters(resetFilters);
    onFilterChange(resetFilters);
    setSearchTerm('');
  };

  // Recherche dans l'historique
  const filteredDeliveries = history?.deliveries?.filter(delivery => {
    if (!searchTerm) return true;
    
    const search = searchTerm.toLowerCase();
    return (
      delivery.tracking_number?.toLowerCase().includes(search) ||
      delivery.recipient_name?.toLowerCase().includes(search) ||
      delivery.recipient_phone?.toLowerCase().includes(search) ||
      delivery.to_address?.toLowerCase().includes(search)
    );
  });

  // Icône de statut
  const getStatusIcon = (status) => {
    switch (status) {
      case DELIVERY_STATUS.DELIVERED:
        return '✅';
      case DELIVERY_STATUS.FAILED:
      case DELIVERY_STATUS.CANCELLED:
        return '❌';
      case DELIVERY_STATUS.RETURNED:
        return '🔄';
      case DELIVERY_STATUS.IN_TRANSIT:
        return '🚚';
      default:
        return '📦';
    }
  };

  // Exporter l'historique (CSV)
  const handleExport = () => {
    if (!filteredDeliveries || filteredDeliveries.length === 0) {
      alert('Aucune donnée à exporter');
      return;
    }

    const csvContent = [
      ['N° Suivi', 'Date', 'Destinataire', 'Adresse', 'Statut', 'Montant'].join(','),
      ...filteredDeliveries.map(d => [
        d.tracking_number,
        formatDate(d.createdAt),
        d.recipient_name,
        `"${d.to_address}"`,
        DELIVERY_STATUS_LABELS[d.status],
        d.cost
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `historique_livraisons_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  if (loading && !history) {
    return <Loading message="Chargement de l'historique..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header avec recherche et filtres */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Barre de recherche */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher par N° suivi, destinataire, téléphone..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Boutons d'action */}
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn btn-secondary flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              Filtres
              {(localFilters.status || localFilters.start_date || localFilters.end_date) && (
                <span className="w-2 h-2 bg-primary-600 rounded-full"></span>
              )}
            </button>

            <button
              onClick={handleExport}
              className="btn btn-secondary flex items-center gap-2"
              disabled={!filteredDeliveries || filteredDeliveries.length === 0}
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Exporter</span>
            </button>
          </div>
        </div>

        {/* Panneau de filtres */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Statut */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Statut
                </label>
                <select
                  value={localFilters.status}
                  onChange={(e) => setLocalFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="input"
                >
                  <option value="">Tous les statuts</option>
                  {Object.entries(DELIVERY_STATUS_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>
                      {getStatusIcon(key)} {label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date début */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date de début
                </label>
                <input
                  type="date"
                  value={localFilters.start_date}
                  onChange={(e) => setLocalFilters(prev => ({ ...prev, start_date: e.target.value }))}
                  className="input"
                />
              </div>

              {/* Date fin */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date de fin
                </label>
                <input
                  type="date"
                  value={localFilters.end_date}
                  onChange={(e) => setLocalFilters(prev => ({ ...prev, end_date: e.target.value }))}
                  className="input"
                />
              </div>
            </div>

            {/* Actions filtres */}
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleApplyFilters}
                className="btn btn-primary"
              >
                Appliquer les filtres
              </button>
              <button
                onClick={handleResetFilters}
                className="btn btn-secondary"
              >
                Réinitialiser
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Résumé */}
      {history && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-blue-800">
            <Package className="w-5 h-5" />
            <span className="font-medium">
              {filteredDeliveries?.length || 0} livraison(s) trouvée(s)
              {history.pagination && ` sur ${history.pagination.total} au total`}
            </span>
          </div>
        </div>
      )}

      {/* Liste des livraisons */}
      {loading ? (
        <Loading message="Chargement..." />
      ) : !filteredDeliveries || filteredDeliveries.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Aucune livraison trouvée
          </h3>
          <p className="text-gray-600">
            {searchTerm || localFilters.status || localFilters.start_date || localFilters.end_date
              ? 'Essayez de modifier vos critères de recherche'
              : 'Vous n\'avez pas encore effectué de livraisons'
            }
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredDeliveries.map((delivery) => (
            <div
              key={delivery.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
            >
              <div className="p-4 sm:p-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Package className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <p className="font-mono font-semibold text-gray-900">
                        {delivery.tracking_number}
                      </p>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDateTime(delivery.createdAt)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className={`badge ${getStatusClass(delivery.status)}`}>
                      {getStatusIcon(delivery.status)} {DELIVERY_STATUS_LABELS[delivery.status]}
                    </span>
                  </div>
                </div>

                {/* Informations */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  {/* Destinataire */}
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Destinataire</p>
                    <p className="font-medium text-gray-900">{delivery.recipient_name}</p>
                    <p className="text-sm text-gray-600">{delivery.recipient_phone}</p>
                  </div>

                  {/* Adresse */}
                  <div>
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      Adresse de livraison
                    </p>
                    <p className="text-sm text-gray-900">{delivery.to_address}</p>
                    {delivery.delivery_zone && (
                      <span className="inline-block mt-1 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                        {delivery.delivery_zone}
                      </span>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-2 text-primary-600 font-semibold">
                    <DollarSign className="w-4 h-4" />
                    {formatCurrency(delivery.cost)}
                  </div>

                  <button
                    onClick={() => setSelectedDelivery(delivery)}
                    className="btn btn-secondary btn-sm flex items-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    Détails
                  </button>
                </div>

                {/* Notes (si échec) */}
                {(delivery.status === DELIVERY_STATUS.FAILED || delivery.status === DELIVERY_STATUS.RETURNED) && delivery.failure_reason && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm font-medium text-red-800 mb-1">
                      Raison de l'échec :
                    </p>
                    <p className="text-sm text-red-700">{delivery.failure_reason}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {history?.pagination && history.pagination.totalPages > 1 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => onPageChange(history.pagination.currentPage - 1)}
              disabled={history.pagination.currentPage === 1 || loading}
              className="btn btn-secondary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
              Précédent
            </button>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                Page {history.pagination.currentPage} sur {history.pagination.totalPages}
              </span>
              <span className="text-xs text-gray-500">
                ({history.pagination.total} résultat{history.pagination.total > 1 ? 's' : ''})
              </span>
            </div>

            <button
              onClick={() => onPageChange(history.pagination.currentPage + 1)}
              disabled={history.pagination.currentPage === history.pagination.totalPages || loading}
              className="btn btn-secondary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Suivant
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Modal de détails */}
      {selectedDelivery && (
        <DeliveryDetailsModal
          delivery={selectedDelivery}
          onClose={() => setSelectedDelivery(null)}
        />
      )}
    </div>
  );
};

// Composant Modal de détails
const DeliveryDetailsModal = ({ delivery, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Détails de la livraison</h2>
            <p className="text-sm text-gray-600 font-mono">{delivery.tracking_number}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6 space-y-6">
          {/* Statut */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Statut</h3>
            <span className={`badge ${getStatusClass(delivery.status)} text-base`}>
              {DELIVERY_STATUS_LABELS[delivery.status]}
            </span>
          </div>

          {/* Informations destinataire */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Destinataire</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex items-start gap-2">
                <span className="text-gray-600 min-w-[120px]">Nom :</span>
                <span className="font-medium text-gray-900">{delivery.recipient_name}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-gray-600 min-w-[120px]">Téléphone :</span>
                <span className="font-medium text-gray-900">{delivery.recipient_phone}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-gray-600 min-w-[120px]">Adresse :</span>
                <span className="font-medium text-gray-900">{delivery.to_address}</span>
              </div>
              {delivery.delivery_zone && (
                <div className="flex items-start gap-2">
                  <span className="text-gray-600 min-w-[120px]">Zone :</span>
                  <span className="badge badge-primary">{delivery.delivery_zone}</span>
                </div>
              )}
            </div>
          </div>

          {/* Informations colis */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Informations colis</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              {delivery.package_description && (
                <div className="flex items-start gap-2">
                  <span className="text-gray-600 min-w-[120px]">Description :</span>
                  <span className="font-medium text-gray-900">{delivery.package_description}</span>
                </div>
              )}
              {delivery.package_weight && (
                <div className="flex items-start gap-2">
                  <span className="text-gray-600 min-w-[120px]">Poids :</span>
                  <span className="font-medium text-gray-900">{delivery.package_weight} kg</span>
                </div>
              )}
              <div className="flex items-start gap-2">
                <span className="text-gray-600 min-w-[120px]">Frais :</span>
                <span className="font-semibold text-primary-600 text-lg">
                  {formatCurrency(delivery.cost)}
                </span>
              </div>
            </div>
          </div>

          {/* Dates */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Chronologie</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex items-start gap-2">
                <span className="text-gray-600 min-w-[120px]">Créée le :</span>
                <span className="font-medium text-gray-900">{formatDateTime(delivery.createdAt)}</span>
              </div>
              {delivery.accepted_at && (
                <div className="flex items-start gap-2">
                  <span className="text-gray-600 min-w-[120px]">Acceptée le :</span>
                  <span className="font-medium text-gray-900">{formatDateTime(delivery.accepted_at)}</span>
                </div>
              )}
              {delivery.driver_started_at && (
                <div className="flex items-start gap-2">
                  <span className="text-gray-600 min-w-[120px]">Récupérée le :</span>
                  <span className="font-medium text-gray-900">{formatDateTime(delivery.driver_started_at)}</span>
                </div>
              )}
              {delivery.actual_delivery_date && (
                <div className="flex items-start gap-2">
                  <span className="text-gray-600 min-w-[120px]">Livrée le :</span>
                  <span className="font-medium text-gray-900">{formatDateTime(delivery.actual_delivery_date)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Notes d'échec */}
          {(delivery.status === DELIVERY_STATUS.FAILED || delivery.status === DELIVERY_STATUS.RETURNED) && delivery.failure_reason && (
            <div>
              <h3 className="text-sm font-semibold text-red-700 mb-3">Raison de l'échec</h3>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">{delivery.failure_reason}</p>
              </div>
            </div>
          )}

          {/* Instructions spéciales */}
          {delivery.delivery_instructions && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Instructions spéciales</h3>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-900">{delivery.delivery_instructions}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
          <button
            onClick={onClose}
            className="btn btn-primary w-full"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default DriverHistory;